import React from "react";
import { Route, Switch } from "react-router-dom";
import { RemoteModuleLoader, Safe, ModuleLoader, ModuleError } from "../remote-modules-loader";
import type { PlaygroundContext } from "@glideapps/lab-playground-module";
import { ModuleLabChrome } from "./module-lab-chrome";
import { useModuleLabBuilder } from "./use-module-lab-builder";
import { ErrorBoundary } from "@glide/common";
import "twin.macro";

const GCS_BUCKET_HOST = "https://storage.googleapis.com/ux-modules-repository";

const entryPoints = {
    dev: (port = 4000) => `http://localhost:${port}/remoteEntry.js`,
    sha: (module: string, sha: string) => `${GCS_BUCKET_HOST}/sha/${module.replace(/_/g, "-")}/${sha}/remoteEntry.js`,

    // Staging and prod can just be used in their respective env:
    default: (module: string) => RemoteModuleLoader.get().getDefaultEntryPoint(module),

    // Unused, so far
    // staging: (module: string) => `${GCS_BUCKET_HOST}/preview/${module.replace(/_/g, "-")}/latest/remoteEntry.js`,
    // prod: (module: string) => `${GCS_BUCKET_HOST}/release/${module.replace(/_/g, "-")}/latest/remoteEntry.js`,
};

export type ModuleLabBuilderContext = {
    orgID?: string;
    appID?: string;
    firebaseUserToken?: string;
};

export type ModuleOption = {
    label: string;
    value: string;
};

const availableModules: ModuleOption[] = [
    { label: "Select a module...", value: "" },
    { label: "Playground", value: "playground" },
    { label: "Agent", value: "agent_ui" },
    { label: "Example", value: "example" },
    { label: "Standalone Demo", value: "demo" },
];

// Future! We could export this from modules
type ExampleModuleProps = {
    name: string;
    builderContext: ModuleLabBuilderContext;
};

type ModuleLabRenderProps = {
    module: string;
    ctx: ModuleLabBuilderContext;
    isDevMode: boolean;
    sha: string | undefined;
};

const ModuleLabRenderModule = ({ module, ctx, isDevMode, sha }: ModuleLabRenderProps) => {
    const getEntryPoint = React.useCallback(
        (moduleName: string) => {
            if (sha !== undefined) {
                return entryPoints.sha(moduleName, sha);
            }
            if (isDevMode) {
                return entryPoints.dev();
            }
            return RemoteModuleLoader.get().getDefaultEntryPoint(moduleName);
        },
        [sha, isDevMode]
    );

    if (module === "") {
        return <div tw="text-center text-text-pale mt-8">Please select a module to view</div>;
    }
    if (module === "example") {
        // The OG Example (Safe to delete later)
        const entryPoint = getEntryPoint("example");
        const ExampleComponent = RemoteModuleLoader.get().loadRemoteComponentWithEntryPoint<ExampleModuleProps>(
            "example",
            "ExampleComponent",
            entryPoint
        );
        return (
            <Safe loading={<ModuleLoader />} error={<ModuleError module={module} />}>
                <ExampleComponent builderContext={ctx} name="Module Lab User" />
            </Safe>
        );
    }
    if (module === "demo") {
        const entryPoint = getEntryPoint("demo");
        const DemoComponent = RemoteModuleLoader.get().loadRemoteComponentWithEntryPoint<any>(
            "demo",
            "ExampleComponentDemo",
            entryPoint
        );
        return (
            <Safe loading={<ModuleLoader />} error={<ModuleError module={module} />}>
                <DemoComponent builderContext={ctx} name="Module Lab User" />
            </Safe>
        );
    }
    if (module === "agent_ui") {
        const entryPoint = getEntryPoint("agent_ui");

        const AgentProvider = RemoteModuleLoader.get().loadRemoteComponentWithEntryPoint<any>(
            "agent_ui",
            "AgentProvider",
            entryPoint
        );
        const Chat = RemoteModuleLoader.get().loadRemoteComponentWithEntryPoint<any>("agent_ui", "Chat", entryPoint);

        return (
            <Safe loading={<ModuleLoader />} error={<ModuleError module={module} />}>
                <AgentProvider>
                    <Chat />
                </AgentProvider>
            </Safe>
        );
    }
    if (module === "playground") {
        const entryPoint = getEntryPoint("lab_playground");

        const Playground = RemoteModuleLoader.get().loadRemoteComponentWithEntryPoint<PlaygroundContext>(
            "lab_playground",
            "Playground",
            entryPoint
        );

        return (
            <Safe loading={<ModuleLoader />} error={<ModuleError module={module} />}>
                <Playground appID={ctx.appID ?? ""} />
            </Safe>
        );
    }
    return null;
};

const ModuleLabContent: React.FC = () => {
    const { selectedModule, setSelectedModule, builderContext, isDevMode, setIsDevMode, sha } = useModuleLabBuilder();

    return (
        <div tw="h-screen flex flex-col">
            <ModuleLabChrome
                selectedModule={(selectedModule ?? "").replace(/-/g, "_")}
                setSelectedModule={setSelectedModule}
                availableModules={availableModules}
                isDevMode={isDevMode}
                setIsDevMode={setIsDevMode}
                isPreviewMode={sha !== undefined}
            />

            {sha && (
                <div tw="bg-n400 text-n700 px-4 py-2 text-builder-xs">
                    <strong>You are previewing sha:</strong> {sha}
                </div>
            )}

            <div tw="flex-1 p-4 overflow-auto bg-bg-back">
                <ErrorBoundary fallback={<div>Error occurred</div>}>
                    <ModuleLabRenderModule
                        module={selectedModule ?? ""}
                        ctx={builderContext}
                        isDevMode={isDevMode}
                        sha={sha}
                    />
                </ErrorBoundary>
            </div>
        </div>
    );
};

export const ModuleLab: React.FC = () => {
    return (
        <Switch>
            <Route exact path="/module-lab/preview/:module/:sha" component={ModuleLabContent} />
            <Route exact path="/module-lab" component={ModuleLabContent} />
            <Route exact path="/module-lab/:orgID" component={ModuleLabContent} />
            <Route exact path="/module-lab/:orgID/:appID" component={ModuleLabContent} />
        </Switch>
    );
};
